import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useAuth } from '../../auth/hooks/useAuth';
import useSessionTimer from '../../hooks/useSessionTimer';
import useSessionModal from '../../hooks/useSessionModal';
import FormModal from '../Modals/FormModal';
import { motion } from 'framer-motion';
import SvgIcon from '../SvgIcon/SvgIcon';
import { primaryRoleLabel } from '../../constants/roleLabels';
import { formatSessionTime } from '../../utils/sessionTime';

const getRoleName = (roles: string[]) => primaryRoleLabel(roles, 'Sin rol');

const SessionModal: React.FC = () => {
  const { timeLeft, refreshSession, sessionExpired } = useSessionTimer();
  const { isModalVisible, closeModal } = useSessionModal();
  const { onLogout } = useAuth();
  const auth = useSelector((state: RootState) => state.auth);
  const modalRef = useRef<HTMLDivElement>(null);

  // cierre al hacer click fuera (solo si no expiró)
  useEffect(() => {
    if (!sessionExpired && isModalVisible) {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          modalRef.current &&
          !modalRef.current.contains(event.target as Node)
        ) {
          closeModal();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isModalVisible, closeModal, sessionExpired]);

  if (!isModalVisible) return null;

  const handleLogout = () => {
    onLogout();
    closeModal();
  };

  const timeLeftLabel = formatSessionTime(timeLeft);

  return (
    <FormModal
      isOpen={isModalVisible}
      onClose={() => !sessionExpired && closeModal()}
      title={sessionExpired ? 'Sesión Expirada' : 'Detalles de la Sesión'}
      maxWidth="max-w-md"
      overlayBg="dark"
      zIndex="top"
    >
      <div
        ref={modalRef}
        className="
        flex
        flex-col
        gap-5
      "
      >
        {/* ===================================================== */}
        {/* 🔹 Estado */}
        {/* ===================================================== */}

        <motion.div
          key={sessionExpired ? 'expired' : 'active'}
          initial={{
            opacity: 0,
            y: 8,
          }}
          animate={{
            opacity: 1,
            y: 0,
          }}
          transition={{ duration: 0.2 }}
          className={`
          rounded-2xl

          border

          px-5
          py-4

          text-center

          ${
            sessionExpired
              ? `
                  border-danger/20
                  bg-danger/10
                `
              : `
                  border-primary/20
                  bg-primary/10
                `
          }
        `}
        >
          <div
            className={`
            mx-auto

            flex
            h-12
            w-12

            items-center
            justify-center

            rounded-full

            ${sessionExpired ? 'bg-danger text-white' : 'bg-primary text-white'}
          `}
          >
            <SvgIcon name="clock" className="h-5 w-5" />
          </div>

          <h3
            className={`
            mt-3

            text-base
            font-semibold

            ${sessionExpired ? 'text-danger' : 'text-primary'}
          `}
          >
            {sessionExpired ? 'Sesión expirada' : timeLeftLabel}
          </h3>

          <p
            className="
            mt-1

            text-sm

            text-body

            dark:text-bodydark
          "
          >
            {sessionExpired
              ? 'Tu sesión ha finalizado. Puedes restablecerla o cerrar sesión.'
              : 'La sesión permanece activa.'}
          </p>
        </motion.div>

        {/* ===================================================== */}
        {/* 🔹 Información */}
        {/* ===================================================== */}

        <div
          className="
          bg-gray-1

          overflow-hidden

          rounded-xl
          border

          border-stroke

          dark:border-strokedark
          dark:bg-meta-4
        "
        >
          <div
            className="
            border-b
            border-stroke

            bg-white/60

            px-4
            py-2.5

            dark:border-strokedark
            dark:bg-boxdark
          "
          >
            <h4
              className="
              text-sm
              font-semibold

              text-black

              dark:text-white
            "
            >
              Información de la sesión
            </h4>
          </div>

          <div className="divide-y divide-stroke dark:divide-strokedark">
            <div
              className="
              flex
              items-center
              justify-between

              gap-4

              px-4
              py-3
            "
            >
              <span
                className="
                text-body

                dark:text-bodydark
              "
              >
                Correo
              </span>

              <span
                className="
                max-w-[210px]

                truncate

                text-right
                font-medium

                text-black

                dark:text-white
              "
              >
                {auth.email}
              </span>
            </div>

            <div
              className="
              flex
              items-center
              justify-between

              px-4
              py-3
            "
            >
              <span
                className="
                text-body

                dark:text-bodydark
              "
              >
                Rol
              </span>

              <span
                className="
                rounded-full

                border
                border-primary/20

                bg-primary/10

                px-3
                py-1

                text-xs
                font-semibold

                text-primary
              "
              >
                {getRoleName(auth.roles)}
              </span>
            </div>
          </div>
        </div>

        {/* ===================================================== */}
        {/* 🔹 Acciones */}
        {/* ===================================================== */}

        <div
          className="
          flex
          flex-col-reverse
          gap-3

          sm:flex-row
          sm:justify-end
        "
        >
          {!sessionExpired && (
            <button
              onClick={closeModal}
              className="
              bg-gray-1

              rounded-xl
              border

              border-stroke

              px-5
              py-2.5

              font-medium

              text-black

              transition-all
              duration-200

              hover:bg-gray

              dark:border-strokedark
              dark:bg-meta-4
              dark:text-white
              dark:hover:bg-boxdark
            "
            >
              Cerrar
            </button>
          )}

          <button
            onClick={refreshSession}
            className="
            rounded-xl

            bg-success

            px-5
            py-2.5

            font-medium
            text-white

            shadow-sm

            transition-all
            duration-200

            hover:bg-success/90
          "
          >
            Restablecer sesión
          </button>

          {sessionExpired && (
            <button
              onClick={handleLogout}
              className="
              rounded-xl

              bg-danger

              px-5
              py-2.5

              font-medium
              text-white

              shadow-sm

              transition-all
              duration-200

              hover:bg-danger/90
            "
            >
              Cerrar sesión
            </button>
          )}
        </div>
      </div>
    </FormModal>
  );
};

export default SessionModal;
